import React, { useState } from 'react'

import PropTypes from 'prop-types'
import { Controller, FormContext, useForm } from 'react-hook-form'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@material-ui/core'

import { FileUploadInput, Label, BaseConfirmationDialog } from 'components'

import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import schema from './schema'

const RiskJustificationForm = ({
  open,
  setOpen,
  onSubmit,
  loading = false,
  incident = {},
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [formData, setFormData] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      justification: incident?.riskAssumptions?.detaling?.justification || '',
      attachments: incident?.riskAssumptions?.detaling?.attachments || null,
    },
  })

  const snackbar = useSnackbar()

  const { control, errors, handleSubmit, reset } = formMethods

  const handleClose = () => {
    reset()
    setFormData(null)
    setShowConfirmDialog(false)
    setOpen(false)
  }

  const handleFormSubmit = (data) => {
    const filteredData = { ...data }

    if (data.attachments && Array.isArray(data.attachments)) {
      filteredData.attachments = data.attachments.filter((file) => !file.id)
    }

    setFormData(filteredData)
    setShowConfirmDialog(true)
  }

  const handleConfirmRisk = async () => {
    if (formData) {
      setIsSubmitting(true)
      try {
        await onSubmit(formData)
        handleClose()
      } catch (error) {
        snackbar.open({
          message: 'Erro ao assumir o risco. Tente novamente.',
          variant: 'error',
        })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleRemoveApiFile = async (fileId) => {
    try {
      await service.dponet.incidentRiskAssumptions.destroyAttachment({
        incidentId: incident.id,
        fileId: fileId,
      })
    } catch (error) {
      snackbar.open({
        message: 'Erro ao remover arquivo. Tente novamente.',
        variant: 'error',
      })
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h4">
            Justificativa para Assumir o Risco
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />

      <FormContext {...formMethods}>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          id="risk-justification-form"
        >
          <DialogContent>
            <Box display="flex" flexDirection="column" gridGap={16}>
              <Label
                title="Justificativa *"
                description="Descreva detalhadamente a justificativa para assumir este risco"
                paddingBoxTitle={0}
              >
                <Controller
                  as={
                    <TextField
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={4}
                      error={!!errors.justification}
                      helperText={errors?.justification?.message}
                      placeholder="Digite aqui a justificativa para assumir o risco..."
                    />
                  }
                  control={control}
                  name="justification"
                />
              </Label>

              <Label
                title="Anexo obrigatório *"
                description="Anexe documentos que fundamentem a decisão de assumir o risco"
                paddingBoxTitle={0}
              >
                <FileUploadInput
                  controlName="attachments"
                  multiple
                  controllerBar
                  hasDownloadFile
                  onRemoveApiFile={handleRemoveApiFile}
                />
              </Label>
            </Box>
          </DialogContent>
        </form>
      </FormContext>

      <Divider />
      <DialogActions>
        <Box
          display="flex"
          width="100%"
          gridGap={8}
          p={2}
          justifyContent="flex-end"
        >
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={loading || isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            form="risk-justification-form"
            disabled={loading || isSubmitting}
          >
            Continuar
          </Button>
        </Box>
      </DialogActions>

      <BaseConfirmationDialog
        open={showConfirmDialog}
        setOpen={setShowConfirmDialog}
        title="Tem certeza que deseja assumir o risco?"
        text="Tem certeza que deseja assumir o risco?"
        actionButtonText="Confirmar"
        variant="warning"
        actionButton={handleConfirmRisk}
        loading={isSubmitting}
      />
    </Dialog>
  )
}

RiskJustificationForm.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  incident: PropTypes.object,
}

export default RiskJustificationForm
